import { isActivePlace } from "@nexxt/app-kit";
import type { LucideIcon } from "lucide-react";
import {
  AlarmClock,
  ArrowLeftRight,
  ArrowRightLeft,
  BadgeCheck,
  Banknote,
  BookUser,
  Boxes,
  Building2,
  Calculator,
  CalendarDays,
  CalendarRange,
  ChartBarStacked,
  ChartColumnStacked,
  ClipboardCheck,
  ClipboardX,
  CloudDownload,
  Contact,
  Database,
  Factory,
  FileCheck,
  FileMinus,
  FileSignature,
  FileText,
  FileUp,
  Gauge,
  GitCompareArrows,
  HandCoins,
  Hourglass,
  House,
  Inbox,
  KeyRound,
  KeySquare,
  Layers,
  ListChecks,
  ListX,
  Mail,
  MailCheck,
  Mails,
  MailX,
  MapPinned,
  Megaphone,
  MessageSquareWarning,
  OctagonAlert,
  Package2,
  PackageCheck,
  PackageMinus,
  PackageOpen,
  PackageSearch,
  PackageX,
  Palette,
  Percent,
  ReceiptText,
  Scale,
  Send,
  ShieldCheck,
  Shirt,
  ShoppingCart,
  SlidersHorizontal,
  Tags,
  TrendingUp,
  TriangleAlert,
  Truck,
  Undo2,
  Users,
  UsersRound,
  UserX,
  Warehouse,
} from "lucide-react";
import { m } from "../../paraglide/messages.js";
import { useMe } from "../features/auth/me";

// The places of the app, once: the sidebar, the ⌘K "Go to" group, the `g` + letter shortcuts and their list read
// this one. Places live in sections, by area of the business; the admin sections make an area of their own, whose
// sidebar shows while one of their places is on display (ADR-114).

/** The app, or the admin sections, whose sidebar shows while one of their places is on display (ADR-114). */
export type Area = "app" | "admin";

type SectionId =
  | "catalog"
  | "sales"
  | "purchasing"
  | "logistics"
  | "directory"
  | "access"
  | "configuration"
  | "integration"
  | "dev";

interface NavSection {
  id: SectionId;
  label: () => string;
  area: Area;
}

/** In the order of the sidebar. */
const sections: NavSection[] = [
  { id: "catalog", label: m.nav_section_catalog, area: "app" },
  { id: "sales", label: m.nav_section_sales, area: "app" },
  { id: "purchasing", label: m.nav_section_purchasing, area: "app" },
  { id: "logistics", label: m.nav_section_logistics, area: "app" },
  { id: "directory", label: m.nav_section_directory, area: "app" },
  // not product copy: local and preview only (ADR-038)
  { id: "dev", label: () => "Dev", area: "app" },
  { id: "access", label: m.nav_section_access, area: "admin" },
  { id: "configuration", label: m.nav_section_configuration, area: "admin" },
  { id: "integration", label: m.nav_section_integration, area: "admin" },
];

export interface NavItem {
  id: string;
  to:
    | "/"
    | "/season-calendar"
    | "/season-blockers"
    | "/linesheets"
    | "/linesheet-invitations"
    | "/products"
    | "/pricing/setup"
    | "/margins"
    | "/attributes"
    | "/attribute-completeness"
    | "/sales-campaigns"
    | "/appointments"
    | "/sales-orders"
    | "/allocation"
    | "/order-confirmations"
    | "/joor-orders"
    | "/credit-decisions"
    | "/blocked-customers"
    | "/sales-order-totals"
    | "/cash-projection"
    | "/hilldun-approvals"
    | "/documents"
    | "/claims"
    | "/short-shipments"
    | "/purchase-orders"
    | "/demand"
    | "/supplier-purchase-totals"
    | "/inbound-progress"
    | "/inbounds"
    | "/warehouse-receipts"
    | "/credit-notes"
    | "/stock-movements"
    | "/stock-adjustments"
    | "/vendor-returns"
    | "/warehouse-executions"
    | "/warehouse-counts"
    | "/stock-availability"
    | "/availability-waiting"
    | "/availability-mismatches"
    | "/fulfillments"
    | "/warehouse-shipments"
    | "/organizations"
    | "/scorecard"
    | "/manufacturers"
    | "/area-managers"
    | "/buying-groups"
    | "/bc-records"
    | "/hubspot-records"
    | "/etaf"
    | "/admin/integration"
    | "/admin/reconciliation"
    | "/erp-files"
    | "/admin/notifications"
    | "/admin/reference"
    | "/admin/parameters"
    | "/admin/gates"
    | "/admin/approvals"
    | "/admin/tolerance-bands"
    | "/admin/allocation-schedules"
    | "/admin/code-mappings"
    | "/admin/notification-templates"
    | "/admin/recipient-lists"
    | "/admin/email-suppressions"
    | "/admin/users"
    | "/admin/roles"
    | "/admin/delegated-access"
    | "/_kitchen-sink"
    | "/_til-simulator";
  label: () => string;
  icon: LucideIcon;
  /** None only for Now and the season calendar, above the sections. */
  section?: SectionId;
  /** Shown only to users with this permission (the gateway checks again, ADR-014). */
  permission?: string;
  /** `g` + this key goes there. */
  key?: string;
}

const items: NavItem[] = [
  { id: "now", to: "/", label: m.nav_now, icon: House, key: "n" },
  // ADR-325: across the domains, so beside Now and in no section
  { id: "season-calendar", to: "/season-calendar", label: m.nav_season_calendar, icon: CalendarRange },
  // ADR-327: beside the calendar, whose deadlines its causes hit
  { id: "season-blockers", to: "/season-blockers", label: m.nav_season_blockers, icon: OctagonAlert },
  {
    id: "linesheets",
    to: "/linesheets",
    label: m.nav_linesheets,
    icon: Layers,
    section: "catalog",
    permission: "linesheet.read",
    key: "l",
  },
  {
    id: "products",
    to: "/products",
    label: m.nav_products,
    icon: Shirt,
    section: "catalog",
    permission: "product.read",
    key: "p",
  },
  {
    id: "attributes",
    to: "/attributes",
    label: m.nav_attributes,
    icon: Tags,
    section: "catalog",
    permission: "attributes.read",
    key: "a",
  },
  {
    id: "attribute-completeness",
    to: "/attribute-completeness",
    label: m.nav_attribute_completeness,
    icon: ListChecks,
    section: "catalog",
    // spec v0.2 §9, P8.4b: the attributes of each brand, read by whoever reads the tasks
    permission: "attributes.read",
  },
  {
    id: "price-setup",
    to: "/pricing/setup",
    label: m.nav_price_setup,
    icon: Percent,
    section: "catalog",
    permission: "pricing.read",
    key: "s",
  },
  {
    id: "margins",
    to: "/margins",
    label: m.nav_margins,
    icon: TrendingUp,
    section: "catalog",
    // B-59, ADR-215: Tomorrow's margins of a season, to review the Price Setup of the next one
    permission: "margin.read",
  },
  {
    id: "invitations",
    to: "/linesheet-invitations",
    label: m.nav_invitations,
    icon: Mails,
    section: "catalog",
    permission: "linesheet.manage",
    key: "v",
  },
  {
    id: "sales-orders",
    to: "/sales-orders",
    label: m.nav_sales_orders,
    icon: ReceiptText,
    section: "sales",
    permission: "sales_order.read",
    // the orders arrive from Joor
    key: "j",
  },
  {
    id: "allocation",
    to: "/allocation",
    label: m.nav_allocation,
    icon: PackageCheck,
    section: "sales",
    permission: "allocation.read",
  },
  {
    id: "order-confirmations",
    to: "/order-confirmations",
    label: m.nav_order_confirmations,
    icon: FileCheck,
    section: "sales",
    // ADR-202: the Customer Service issues the confirmations of a brand and a season
    permission: "sales_order.read",
  },
  {
    id: "joor-orders",
    to: "/joor-orders",
    label: m.nav_joor_orders,
    icon: Inbox,
    section: "sales",
    permission: "sales_order.read",
  },
  {
    id: "credit-decisions",
    to: "/credit-decisions",
    label: m.nav_credit_decisions,
    icon: HandCoins,
    section: "sales",
    permission: "credit.read",
  },
  {
    id: "blocked-customers",
    to: "/blocked-customers",
    label: m.nav_blocked_customers,
    icon: UserX,
    section: "sales",
    // spec v0.2 §9, P8.10a: Credit Control's view, read by whoever reads the credit decisions
    permission: "credit.read",
  },
  {
    id: "sales-order-totals",
    to: "/sales-order-totals",
    label: m.nav_sales_order_totals,
    icon: ChartColumnStacked,
    section: "sales",
    // spec v0.3 §9, ADR-261: the orders by season and status, for whoever reads the orders
    permission: "sales_order.read",
  },
  {
    id: "cash-projection",
    to: "/cash-projection",
    label: m.nav_cash_projection,
    icon: Banknote,
    section: "sales",
    // spec v0.3 §9, C-55, ADR-261: Finance's and Credit Control's view, read by whoever reads the credit
    permission: "credit.read",
  },
  {
    id: "hilldun-approvals",
    to: "/hilldun-approvals",
    label: m.nav_hilldun_approvals,
    icon: BadgeCheck,
    section: "sales",
    // ADR-185: Credit Control records what Hilldun said, from a file
    permission: "credit.manage",
  },
  {
    id: "documents",
    to: "/documents",
    label: m.nav_documents,
    icon: FileText,
    section: "sales",
    permission: "document.read",
    // the letters of "documents" are taken by other places
    key: "q",
  },
  {
    id: "claims",
    to: "/claims",
    label: m.nav_claims,
    icon: MessageSquareWarning,
    section: "sales",
    // B-55, ADR-228: the claims of the customers, with the state of their ticket on Zendesk
    permission: "claim.read",
  },
  {
    id: "short-shipments",
    to: "/short-shipments",
    label: m.nav_short_shipments,
    icon: PackageMinus,
    section: "sales",
    // ADR-199: Finance credits what an invoiced shipment did not ship
    permission: "short_shipment.manage",
  },
  {
    id: "sales-campaigns",
    to: "/sales-campaigns",
    label: m.nav_sales_campaigns,
    icon: Megaphone,
    section: "sales",
    permission: "booking.read",
    key: "c",
  },
  {
    id: "appointments",
    to: "/appointments",
    label: m.nav_appointments,
    icon: CalendarDays,
    section: "sales",
    permission: "booking.read",
    key: "b",
  },
  {
    id: "purchase-orders",
    to: "/purchase-orders",
    label: m.nav_purchase_orders,
    icon: ShoppingCart,
    section: "purchasing",
    permission: "purchase_order.read",
    key: "o",
  },
  {
    id: "demand",
    to: "/demand",
    label: m.nav_demand,
    icon: Calculator,
    section: "purchasing",
    // the letters of "demand" are taken by other places
    permission: "purchase_order.read",
  },
  {
    id: "supplier-totals",
    to: "/supplier-purchase-totals",
    label: m.nav_supplier_totals,
    icon: ChartBarStacked,
    section: "purchasing",
    permission: "purchase_order.read",
  },
  {
    id: "inbound-progress",
    to: "/inbound-progress",
    label: m.nav_inbound_progress,
    icon: Warehouse,
    section: "purchasing",
    // spec v0.2 §9, P8.10a: the POs coming in, read by whoever reads the inbounds
    permission: "inbound.read",
  },
  {
    id: "inbounds",
    to: "/inbounds",
    label: m.nav_inbounds,
    icon: PackageOpen,
    section: "purchasing",
    permission: "inbound.read",
  },
  {
    id: "warehouse-receipts",
    to: "/warehouse-receipts",
    label: m.nav_warehouse_receipts,
    icon: PackageSearch,
    section: "purchasing",
    // Tomorrow's alone, as the receipts that wait (ADR-138)
    permission: "inbound.check",
  },
  {
    id: "credit-notes",
    to: "/credit-notes",
    label: m.nav_credit_notes,
    icon: FileMinus,
    section: "purchasing",
    // ADR-140: Finance's list, read by whoever reads the inbounds
    permission: "inbound.read",
  },
  {
    id: "stock-movements",
    to: "/stock-movements",
    label: m.nav_stock_movements,
    icon: ArrowRightLeft,
    section: "logistics",
    permission: "movement.read",
    // the letters of "stock movements" are taken by other places
  },
  {
    id: "stock-adjustments",
    to: "/stock-adjustments",
    label: m.nav_stock_adjustments,
    icon: ClipboardCheck,
    section: "logistics",
    // ADR-226: the adjustments the IMS proposes are movements of stock, Tomorrow's alone
    permission: "movement.read",
  },
  {
    id: "vendor-returns",
    to: "/vendor-returns",
    label: m.nav_vendor_returns,
    icon: Undo2,
    section: "logistics",
    // ADR-227: Logistics opens them, Operations decides a refusal, Finance reads them for the credit note
    permission: "vendor_return.read",
  },
  {
    id: "warehouse-counts",
    to: "/warehouse-counts",
    label: m.nav_warehouse_counts,
    icon: ClipboardX,
    section: "logistics",
    // ADR-226: the counts that wait are Tomorrow's alone, as the adjustments
    permission: "movement.read",
  },
  {
    id: "warehouse-executions",
    to: "/warehouse-executions",
    label: m.nav_warehouse_executions,
    icon: PackageX,
    section: "logistics",
    // ADR-142: the executions that wait are Tomorrow's alone, as the movements
    permission: "movement.read",
  },
  {
    id: "stock-availability",
    to: "/stock-availability",
    label: m.nav_stock_availability,
    icon: Boxes,
    section: "logistics",
    permission: "stock.read",
  },
  {
    id: "availability-waiting",
    to: "/availability-waiting",
    label: m.nav_availability_waiting,
    icon: ListX,
    section: "logistics",
    permission: "stock.read",
  },
  {
    id: "availability-mismatches",
    to: "/availability-mismatches",
    label: m.nav_availability_mismatches,
    icon: Scale,
    section: "logistics",
    // B-92, ADR-242: what a full publication of the IMS found different from the events, checked by Logistics
    permission: "stock.read",
  },
  {
    id: "fulfillments",
    to: "/fulfillments",
    label: m.nav_fulfillments,
    icon: Package2,
    section: "logistics",
    // ADR-152: the shipments of the orders, read by Logistics and the Customer Service, who books the DDP by hand
    permission: "fulfillment.read",
  },
  {
    id: "warehouse-shipments",
    to: "/warehouse-shipments",
    label: m.nav_warehouse_shipments,
    icon: Truck,
    section: "logistics",
    // ADR-130: the shipments that wait, read by whoever reads the fulfillments
    permission: "fulfillment.read",
  },
  {
    id: "organizations",
    to: "/organizations",
    label: m.nav_organizations,
    icon: BookUser,
    section: "directory",
    permission: "organization.read",
    key: "d",
  },
  // ADR-326: how the brands and suppliers kept to what Nexxt asks of them, by season
  {
    id: "scorecard",
    to: "/scorecard",
    label: m.nav_scorecard,
    icon: Gauge,
    section: "directory",
    permission: "organization.read",
  },
  {
    id: "manufacturers",
    to: "/manufacturers",
    label: m.nav_manufacturers,
    icon: Factory,
    section: "directory",
    permission: "organization.read",
    key: "f",
  },
  {
    id: "area-managers",
    to: "/area-managers",
    label: m.nav_area_managers,
    icon: MapPinned,
    section: "directory",
    // ADR-246: the area manager of the orders shipped to each country, and its sales territory (ADR-289)
    permission: "organization.read",
  },
  {
    id: "buying-groups",
    to: "/buying-groups",
    label: m.nav_buying_groups,
    icon: UsersRound,
    section: "directory",
    permission: "organization.read",
    key: "y",
  },
  {
    id: "bc-records",
    to: "/bc-records",
    label: m.nav_bc_records,
    icon: Building2,
    section: "directory",
    permission: "organization.read",
    key: "k",
  },
  {
    id: "hubspot-records",
    to: "/hubspot-records",
    label: m.nav_hubspot_records,
    icon: CloudDownload,
    section: "directory",
    permission: "organization.read",
    key: "h",
  },
  {
    id: "etaf",
    to: "/etaf",
    label: m.nav_etaf,
    icon: FileSignature,
    section: "directory",
    permission: "etaf.read",
    key: "w",
  },
  {
    id: "users",
    to: "/admin/users",
    label: m.nav_users,
    icon: Users,
    section: "access",
    permission: "user.read",
    key: "u",
  },
  {
    id: "roles",
    to: "/admin/roles",
    label: m.nav_roles,
    icon: KeyRound,
    section: "access",
    permission: "user.read",
    key: "e",
  },
  {
    id: "delegated-access",
    to: "/admin/delegated-access",
    label: m.nav_delegated_access,
    icon: KeySquare,
    section: "access",
    // ADR-262: every token and connection of every user
    permission: "delegated_access.manage",
  },
  {
    id: "parameters",
    to: "/admin/parameters",
    label: m.nav_parameters,
    icon: SlidersHorizontal,
    section: "configuration",
    permission: "config.read",
    key: "m",
  },
  {
    id: "gates",
    to: "/admin/gates",
    label: m.nav_gates,
    icon: ShieldCheck,
    section: "configuration",
    permission: "config.read",
    key: "t",
  },
  {
    id: "approvals",
    to: "/admin/approvals",
    label: m.nav_waiting_gates,
    icon: Hourglass,
    section: "configuration",
    // B-60, ADR-224: every role of Backstage reads what waits for an approval
    permission: "gate.read",
  },
  {
    id: "tolerance-bands",
    to: "/admin/tolerance-bands",
    label: m.nav_tolerance_bands,
    icon: Percent,
    section: "configuration",
    // configuration of Tomorrow (V-02, ADR-128): who sets the bands; the suppliers read them from the PO
    permission: "inbound.configure",
  },
  {
    id: "allocation-schedules",
    to: "/admin/allocation-schedules",
    label: m.nav_allocation_schedules,
    icon: AlarmClock,
    section: "configuration",
    // ADR-178 §1.3, ADR-186: Operations sets when the runs of the allocation start by themselves
    permission: "allocation.configure",
  },
  {
    id: "code-mappings",
    to: "/admin/code-mappings",
    label: m.nav_code_mappings,
    icon: ArrowLeftRight,
    section: "configuration",
    permission: "config.read",
    key: "x",
  },
  {
    id: "reference",
    to: "/admin/reference",
    label: m.nav_reference,
    icon: Database,
    section: "configuration",
    permission: "reference.read",
    key: "r",
  },
  {
    id: "notification-templates",
    to: "/admin/notification-templates",
    label: m.nav_notification_templates,
    icon: Mail,
    section: "configuration",
    permission: "notification.read",
  },
  {
    id: "recipient-lists",
    to: "/admin/recipient-lists",
    label: m.nav_recipient_lists,
    icon: Contact,
    section: "configuration",
    permission: "notification.read",
  },
  {
    id: "email-suppressions",
    to: "/admin/email-suppressions",
    label: m.nav_email_suppressions,
    icon: MailX,
    section: "configuration",
    permission: "notification.read",
  },
  {
    id: "integration",
    to: "/admin/integration",
    label: m.nav_integration,
    icon: TriangleAlert,
    section: "integration",
    permission: "integration.read",
    key: "i",
  },
  {
    id: "reconciliation",
    to: "/admin/reconciliation",
    label: m.nav_reconciliation,
    icon: GitCompareArrows,
    section: "integration",
    permission: "integration.read",
  },
  {
    id: "erp-files",
    to: "/erp-files",
    label: m.nav_erp_files,
    icon: FileUp,
    section: "integration",
    permission: "erp_file.queue",
  },
  {
    id: "notifications",
    to: "/admin/notifications",
    label: m.nav_notifications,
    icon: MailCheck,
    section: "integration",
    permission: "notification.read",
  },
  ...(import.meta.env.NEXXT_DEV_TOOLS
    ? [
        {
          id: "kitchen-sink",
          to: "/_kitchen-sink" as const,
          label: () => "Kitchen sink",
          icon: Palette,
          section: "dev" as const,
        },
        {
          id: "til-simulator",
          to: "/_til-simulator" as const,
          label: () => "Simulate TIL",
          icon: Send,
          section: "dev" as const,
        },
      ]
    : []),
];

/** The places the signed-in user can open. */
export function useNavItems(): NavItem[] {
  const { permissions } = useMe();
  return items.filter((item) => !item.permission || permissions.includes(item.permission));
}

interface PlacesOfSection {
  section: NavSection;
  places: NavItem[];
}

/** The sections of an area with the places the user can open; a section with none is left out. */
export const sectionsOf = (places: NavItem[], area: Area): PlacesOfSection[] =>
  sections
    .filter((section) => section.area === area)
    .map((section) => ({ section, places: places.filter((place) => place.section === section.id) }))
    .filter(({ places }) => places.length > 0);

const sectionOf = (place: NavItem) => sections.find((section) => section.id === place.section);

/** The name of a place's section, the hint of ⌘K ("Purchasing"). */
export const sectionLabel = (place: NavItem): string | undefined => sectionOf(place)?.label();

/** The area of the place on display: its section says it, so a place moves area with its section and nothing else. */
export const areaOf = (place: NavItem | undefined): Area => (place && sectionOf(place)?.area) ?? "app";

/** The place on display: the one whose route is the page's, or above it (a linesheet is in Linesheets). */
export const placeOn = (places: NavItem[], pathname: string): NavItem | undefined =>
  places.find((place) => isActivePlace(place.to, pathname));
